import React from 'react';
import { useI18n } from '../../context/I18nContext';
import { PoloEditor } from './PoloEditor';
import { Icons } from '../icons/Icons';
import { formatSezione, getMultipolarSeparator } from '../../utils/cableLogic';
import SheathSelector from './SheathSelector';

const MultipolarCableForm = ({
  currentMultipolarCable,
  setCurrentMultipolarCable,
  addPolo,
  settings,
  terminalData,
  cables,
  onAddMultipolarCable,
  editingMultipolarIndex,
  onCancelEdit,
  updatePolo,
  removePolo,
  duplicatePolo,
  addMorsettoDirection,
  addVirtualConnection,
  onSaveAsNewMultipolarCable,
  swapAB,
  availableSheaths,
  onAddSheath,
  onDeleteSheath
}) => {
  const { t } = useI18n();
  const safeCable = currentMultipolarCable || { gruppo: '', lunghezza: 200, nomeCavo: '', poli: [] };
  const poli = Array.isArray(safeCable.poli) ? safeCable.poli : [];
  const catalogLocked = !!safeCable.catalogAssignment;

  return (
    <div className="h-full flex flex-col">
      {/* Header con campi principali - FISSO */}
      <div className="flex-shrink-0 space-y-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('cableForm.cableName')} <span className="text-xs text-gray-500">{t('cableForm.optional')}</span>
            </label>
            <input
              type="text"
              value={safeCable.nomeCavo || ''}
              onChange={(e) => setCurrentMultipolarCable(prev => ({
                ...prev,
                nomeCavo: e.target.value
              }))}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder={`${t('cableForm.placeholderCableName')} ${poli.length}${getMultipolarSeparator(poli)}${formatSezione(poli[0]?.sezione || 0.5, settings.useAWG)}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('cableForm.group')}</label>
            <input
              type="text"
              value={safeCable.gruppo || ''}
              onChange={(e) => setCurrentMultipolarCable(prev => ({
                ...prev,
                gruppo: e.target.value
              }))}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder={t('cableForm.placeholderGroup')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('cableForm.baseLength')}</label>
            <input
              type="number"
              value={safeCable.lunghezza || 0}
              onChange={(e) => {
                const newLength = parseInt(e.target.value) || 200;
                setCurrentMultipolarCable(prev => ({
                  ...prev,
                  lunghezza: newLength,
                  poli: prev.poli.map(polo => ({
                    ...polo,
                    lunghezzaA: polo.lunghezzaA === prev.lunghezza ? newLength : polo.lunghezzaA,
                    lunghezzaB: polo.lunghezzaB === prev.lunghezza ? newLength : polo.lunghezzaB
                  }))
                }));
              }}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Campo Guaina */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t('cableForm.sheath')}
            <span className="text-xs text-gray-500 ml-1">{t('cableForm.optional')}</span>
          </label>
          <SheathSelector
            value={safeCable.guaina}
            onChange={(guainaId) => setCurrentMultipolarCable(prev => ({ ...prev, guaina: guainaId }))}
            availableSheaths={availableSheaths || []}
            onAddSheath={onAddSheath}
            onDeleteSheath={onDeleteSheath}
            className="w-full"
          />
        </div>
      </div>

      {/* Sezione poli */}
      <div className="flex-1 bg-gray-50 rounded-lg p-4 flex flex-col min-h-0">
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h3 className="text-lg font-medium text-gray-800">
            {t('cableForm.poles')} ({poli.length})
          </h3>
          <button
            onClick={addPolo}
            disabled={catalogLocked}
            className={`p-1 rounded transition-colors ${catalogLocked ? 'text-gray-300 cursor-not-allowed' : 'text-green-600 hover:bg-green-100'}`}
            title={t('cableForm.addNewPole')}
          >
            <Icons.Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Lista poli con scroll - ALTEZZA FISSA */}
        <div className="overflow-y-auto space-y-4 h-[525px] pr-2" style={{ minHeight: 0 }}>
          {poli.map((polo, index) => (
            <div key={polo.id} className="w-full">
              <PoloEditor
                polo={polo}
                index={index}
                onUpdate={updatePolo}
                onRemove={removePolo}
                onDuplicate={duplicatePolo}
                settings={settings}
                terminalData={terminalData}
                currentMultipolarCable={safeCable}
                cables={cables}
                editingMultipolarIndex={editingMultipolarIndex}
                canRemove={poli.length > 1}
                addMorsettoDirection={addMorsettoDirection}
                addVirtualConnection={addVirtualConnection}
                swapAB={swapAB}
                catalogLocked={catalogLocked}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Pulsanti azione */}
      <div className="flex-shrink-0 mt-6 pt-4 border-t border-gray-200">
        <div className="flex gap-3">
          <button
            onClick={onAddMultipolarCable}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            {editingMultipolarIndex >= 0 ? (
              <>
                <Icons.Save className="w-4 h-4" />
                {t('cableForm.saveChanges')}
              </>
            ) : (
              <>
                <Icons.Plus className="w-4 h-4" />
                {t('cableForm.addMultipolarCable')}
              </>
            )}
          </button>

          {editingMultipolarIndex >= 0 && (
            <>
              <button
                onClick={onSaveAsNewMultipolarCable}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Icons.Plus className="w-4 h-4" />
                {t('cableForm.saveAsNew')}
              </button>
              <button
                onClick={onCancelEdit}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Icons.X className="w-4 h-4" />
                {t('cableForm.cancelEdit')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultipolarCableForm;
